/* eslint-disable consistent-return */
/**
 * Shared file upload validation: images and documents max 5 MB.
 * Use for all file inputs across the app; show FILE_SIZE_LIMIT_MESSAGE in UI.
 */

export const MAX_FILE_SIZE_MB = 5;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/** Message to show next to file upload (e.g. "Maximum file size: 5 MB") */
export const FILE_SIZE_LIMIT_MESSAGE = `Maximum file size: ${MAX_FILE_SIZE_MB} MB`;

/**
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateFileSize = (file) => {
  if (!file || typeof file.size !== 'number') {
    return { valid: false, error: 'Invalid file.' };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: `File exceeds ${MAX_FILE_SIZE_MB} MB. Please choose a smaller file.`,
    };
  }
  return { valid: true };
};

/**
 * Validate multiple files; returns first error or valid.
 * @param {File[]} files
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateFilesSize = (files) => {
  if (!Array.isArray(files) || files.length === 0) {
    return { valid: true };
  }
  for (let i = 0; i < files.length; i += 1) {
    const result = validateFileSize(files[i]);
    if (!result.valid) {
      return result;
    }
  }
  return { valid: true };
};

/**
 * Filter files to only those under the size limit; returns { validFiles, rejectedNames }.
 * @param {File[]} files
 * @returns {{ validFiles: File[], rejectedNames: string[] }}
 */
export const filterFilesBySize = (files) => {
  const validFiles = [];
  const rejectedNames = [];
  const list = Array.isArray(files) ? files : [];
  list.forEach((file) => {
    const result = validateFileSize(file);
    if (result.valid) {
      validFiles.push(file);
    } else {
      rejectedNames.push(file.name);
    }
  });
  return { validFiles, rejectedNames };
};

/** Media page: Banner - JPG/PNG max 5MB */
export const BANNER_JPG_MAX_BYTES = 5 * 1024 * 1024;
export const BANNER_PNG_MAX_BYTES = 5 * 1024 * 1024;

/** Media page: Gallery & Logo - PNG/JPG max 5MB */
export const GALLERY_LOGO_MAX_BYTES = 5 * 1024 * 1024;

/**
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateBannerFile = (file) => {
  if (!file || typeof file.size !== 'number') return { valid: false, error: 'Invalid file.' };
  const type = (file.type || '').toLowerCase();
  const isJpeg = type === 'image/jpeg' || type === 'image/jpg';
  const isPng = type === 'image/png';
  if (!isJpeg && !isPng) {
    return { valid: false, error: 'Banner must be JPG or PNG.' };
  }
  if (isJpeg && file.size > BANNER_JPG_MAX_BYTES) {
    return {
      valid: false,
      error: 'File exceeds 5 MB for JPG. Please choose a smaller file.',
    };
  }
  if (isPng && file.size > BANNER_PNG_MAX_BYTES) {
    return {
      valid: false,
      error: 'File exceeds 5 MB for PNG. Please choose a smaller file.',
    };
  }
  return { valid: true };
};

/**
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateGalleryLogoFile = (file) => {
  if (!file || typeof file.size !== 'number') return { valid: false, error: 'Invalid file.' };
  if (file.size > GALLERY_LOGO_MAX_BYTES) {
    return {
      valid: false,
      error: 'File exceeds 5 MB. Recommended: PNG with transparent background, max 5 MB.',
    };
  }
  const type = (file.type || '').toLowerCase();
  const isJpeg = type === 'image/jpeg' || type === 'image/jpg';
  const isPng = type === 'image/png';
  const isAmbiguousMime = !type || type === 'application/octet-stream';
  if (!isJpeg && !isPng && !isAmbiguousMime) {
    return {
      valid: false,
      error: 'File must be a JPG or PNG image.',
    };
  }
  return { valid: true };
};

/** JPEG file signature (SOI + next marker). */
const JPEG_MAGIC = [0xff, 0xd8, 0xff];
/** PNG file signature. */
const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/**
 * Confirms binary content starts like a real JPEG or PNG (not a renamed non-image).
 * @param {File} file
 * @returns {Promise<{ valid: boolean, error?: string }>}
 */
export const validateImageMagicBytes = async (file) => {
  if (!file || typeof file.slice !== 'function') {
    return { valid: false, error: 'Invalid file.' };
  }
  try {
    const buf = await file.slice(0, 12).arrayBuffer();
    const bytes = new Uint8Array(buf);
    if (bytes.length < 3) {
      return {
        valid: false,
        error: 'File is empty or too small to be an image.',
      };
    }
    const isJpeg = JPEG_MAGIC.every((b, i) => bytes[i] === b);
    const isPng = bytes.length >= 8 && PNG_MAGIC.every((b, i) => bytes[i] === b);
    if (isJpeg || isPng) return { valid: true };
    return {
      valid: false,
      error: 'File is not a valid JPEG or PNG. It may be corrupted or renamed.',
    };
  } catch {
    return { valid: false, error: 'File could not be read.' };
  }
};

/**
 * Ensures the browser can decode the file as a bitmap (catches truncated / corrupt payloads).
 * @param {File} file
 * @returns {Promise<{ valid: boolean, error?: string, width?: number, height?: number }>}
 */
export const verifyDecodableImage = (file) =>
  new Promise((resolve) => {
    const fail = (error) => resolve({ valid: false, error });

    const finishOk = (width, height) =>
      resolve({
        valid: true,
        width,
        height,
      });

    if (typeof createImageBitmap === 'function') {
      createImageBitmap(file)
        .then((bitmap) => {
          const w = bitmap.width;
          const h = bitmap.height;
          bitmap.close();
          finishOk(w, h);
        })
        .catch(() =>
          fail('Image could not be decoded. It may be corrupted or not a valid picture file.')
        );
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      finishOk(img.naturalWidth, img.naturalHeight);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      fail('Image could not be decoded.');
    };
    img.src = url;
  });

/**
 * Magic bytes + decode check for Media page uploads (JPG/PNG only).
 * @param {File} file
 * @returns {Promise<{ valid: boolean, error?: string }>}
 */
export const validateImageFileIntegrity = async (file) => {
  const magic = await validateImageMagicBytes(file);
  if (!magic.valid) return magic;
  const decoded = await verifyDecodableImage(file);
  if (!decoded.valid) return decoded;
  return { valid: true };
};

const BANNER_REQUIRED_WIDTH = 1080;
const BANNER_REQUIRED_HEIGHT = 1920;
const DIMENSION_TOLERANCE = 100;

/**
 * Load image and check that it is within ±100px of 1080×1920px (9:16 portrait).
 * Accepts width: 980–1180px and height: 1820–2020px.
 * @param {File} file
 * @returns {Promise<{ is9x16: boolean, message?: string }>}
 */
export const checkBannerAspectRatio = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const isValid =
        Math.abs(img.width - BANNER_REQUIRED_WIDTH) <= DIMENSION_TOLERANCE &&
        Math.abs(img.height - BANNER_REQUIRED_HEIGHT) <= DIMENSION_TOLERANCE;
      resolve({
        is9x16: isValid,
        message: isValid
          ? undefined
          : `Banner must be ${BANNER_REQUIRED_WIDTH}×${BANNER_REQUIRED_HEIGHT}px (±${DIMENSION_TOLERANCE}px). Yours is ${img.width}×${img.height}px.`,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({
        is9x16: false,
        message: 'Could not read this image. It may be corrupted.',
      });
    };
    img.src = url;
  });

const LANDSCAPE_REQUIRED_WIDTH = 1920;
const LANDSCAPE_REQUIRED_HEIGHT = 1080;

/**
 * Load image and verify it is within ±100px of 1920×1080px (16:9 landscape).
 * Accepts width: 1820–2020px and height: 980–1180px.
 * Used for Gallery Images uploads.
 * @param {File} file
 * @returns {Promise<{ isValid: boolean, message?: string }>}
 */
export const checkLandscapeResolution = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const isValid =
        Math.abs(img.width - LANDSCAPE_REQUIRED_WIDTH) <= DIMENSION_TOLERANCE &&
        Math.abs(img.height - LANDSCAPE_REQUIRED_HEIGHT) <= DIMENSION_TOLERANCE;
      resolve({
        isValid,
        message: isValid
          ? undefined
          : `Image must be ${LANDSCAPE_REQUIRED_WIDTH}×${LANDSCAPE_REQUIRED_HEIGHT}px (±${DIMENSION_TOLERANCE}px). Yours is ${img.width}×${img.height}px.`,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({
        isValid: false,
        message: 'Could not read this image. It may be corrupted.',
      });
    };
    img.src = url;
  });

/**
 * Check if PNG has transparent pixels (alpha channel used).
 * @param {File} file
 * @returns {Promise<{ hasTransparency: boolean, message?: string }>}
 */
export const checkPngTransparency = (file) => {
  const type = (file.type || '').toLowerCase();
  if (type !== 'image/png') return Promise.resolve({ hasTransparency: true });
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        return resolve({ hasTransparency: true });
      }
      ctx.drawImage(img, 0, 0);
      const { data } = ctx.getImageData(0, 0, Math.min(50, img.width), Math.min(50, img.height));
      let hasAlpha = false;
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) {
          hasAlpha = true;
          break;
        }
      }
      URL.revokeObjectURL(url);
      resolve({
        hasTransparency: hasAlpha,
        message: hasAlpha
          ? undefined
          : 'Recommended: PNG with transparent background for best results.',
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({
        hasTransparency: true,
        decodeError: 'Could not read this image.',
      });
    };
    img.src = url;
  });
};
